import type { ListBeneficiariesData, ListBeneficiariesResponseDtoOutput } from '@peanuds/shared';
import { peanudsApi, withAuthenticatedHeaders } from '@peanuds/shared';

const BENEFICIARIES_PAGE_SIZE = 100;

type Beneficiary = ListBeneficiariesResponseDtoOutput['data'][number];

export type BeneficiariesSortOrder = NonNullable<
  NonNullable<ListBeneficiariesData['query']>['sortOrder']
>;

export const listAllBeneficiaries = async (sortOrder: BeneficiariesSortOrder | null = null) => {
  const sortQuery = sortOrder === null ? {} : { sortBy: 'fullName' as const, sortOrder };
  const beneficiaries: Beneficiary[] = [];
  let offset = 0;
  let lastResponse: Awaited<ReturnType<typeof peanudsApi.listBeneficiaries<false>>>;

  while (true) {
    lastResponse = await peanudsApi.listBeneficiaries(
      await withAuthenticatedHeaders<ListBeneficiariesData, false>({
        query: { offset, limit: BENEFICIARIES_PAGE_SIZE, ...sortQuery },
      }),
    );

    if (lastResponse instanceof Error || lastResponse.error !== undefined) {
      return lastResponse;
    }

    const page = lastResponse.data;
    if (page.offset !== offset) {
      return new Error('Beneficiary pagination returned an unexpected offset');
    }

    beneficiaries.push(...page.data);
    const nextOffset = page.offset + page.data.length;
    if (page.data.length < page.limit || (page.total !== undefined && nextOffset >= page.total)) {
      break;
    }
    if (nextOffset <= offset) {
      return new Error('Beneficiary pagination did not advance');
    }
    offset = nextOffset;
  }

  return {
    ...lastResponse,
    data: {
      ...lastResponse.data,
      offset: 0,
      limit: beneficiaries.length,
      data: beneficiaries,
    },
  };
};
